import {
  AddItemsFilters,
  AddItemsTable,
  GetCampaignItemsColumnsFn,
} from '@retail/campaign/components';
import { Stack, Typography } from '@mui/material';
import { useDisclosure } from '@shared/hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  FilterChipContainer,
  ToggleFiltersToolbar,
  ToggleFiltersToolbarButton,
} from '@retail/components';
import { useAppTFunction } from '@retail/app/i18n';
import { useSearchFilters } from '@retail/products/context';
import { BaseMgSupplierCampaignItem } from '@retail/campaign/types';
import { useItemsFilter } from './useItemsFilter';
import { useLanguageSelector } from '@retail/app/hooks';
import { ProductGroupCount } from '@retail/products/types';
import { searchFilters } from '@retail/products/utils';

interface Props<TMgSupplierCampaignItem extends BaseMgSupplierCampaignItem> {
  items: BaseMgSupplierCampaignItem[];
  getCampaignItemsColumns: GetCampaignItemsColumnsFn<TMgSupplierCampaignItem>;
}

export const AddItemsOverviewContainer = <
  TMgSupplierCampaignItem extends BaseMgSupplierCampaignItem
>({
  items,
  getCampaignItemsColumns,
}: Props<TMgSupplierCampaignItem>) => {
  const t = useAppTFunction();
  const { language } = useLanguageSelector();
  const { isOpen: showFilters, onToggle: toggleFilters } = useDisclosure(false);
  const { isOpen: includeVat, onToggle: toggleIncludeVat } = useDisclosure(true);

  const { filterOptions } = useSearchFilters();

  const [removedItems, setRemovedItems] = useState<number[]>([]);

  const [gridKey, setGridKey] = useState(0);

  useEffect(() => {
    // Re-render data grid whenever items changes to force data grid sorting
    setGridKey((prev) => prev + 1);
  }, [items]);

  const formMethods = useFormContext();

  const removeItem = useCallback(
    (mgSupplierItemId: number) => {
      setRemovedItems((prev) => [...prev, mgSupplierItemId]);
      const formRows = formMethods.getValues('items');
      delete formRows[mgSupplierItemId];
      formMethods.setValue('items', formRows);
    },
    [formMethods]
  );

  const campaignItems = useMemo(
    () => items?.filter((item) => !removedItems.includes(item.mgSupplierItemId)),
    [items, removedItems]
  );

  const itemColumns = useMemo(
    () => getCampaignItemsColumns({ incVat: includeVat, deleteItem: removeItem }),
    [getCampaignItemsColumns, includeVat, removeItem]
  );

  const { filteredItems } = useItemsFilter({ items: campaignItems ?? [] });

  const productGroupsInSelection: ProductGroupCount[] = useMemo(() => {
    const campaignGroupNumbers = new Set(
      campaignItems?.map((item) => item.productGroup.groupNumber)
    );

    return ProductGroupCount.filterMatchingProductGroupsDeep(
      filterOptions.productGroupHierarchy,
      campaignGroupNumbers
    );
  }, [campaignItems, filterOptions.productGroupHierarchy]);

  return (
    <Stack flexGrow={1} height="100%" pt={2}>
      <ToggleFiltersToolbar sx={{ justifyContent: 'space-between' }}>
        <FilterChipContainer allowedKeys={searchFilters} filterOptions={{ ...filterOptions }} />
        <Stack direction="row" alignItems="center" gap={2}>
          <Typography noWrap>{t('calculus.nrOfItems', { count: filteredItems.length })}</Typography>
          <ToggleFiltersToolbarButton showFilters={showFilters} toggleFilters={toggleFilters} />
        </Stack>
      </ToggleFiltersToolbar>

      <Stack direction="row" flexGrow={1}>
        <AddItemsTable
          key={gridKey}
          items={filteredItems}
          language={language}
          columns={itemColumns}
        />

        <AddItemsFilters
          includeVat={includeVat}
          toggleIncludeVat={toggleIncludeVat}
          showFilters={showFilters}
          filterOptions={{ ...filterOptions, productGroupHierarchy: productGroupsInSelection }}
        />
      </Stack>
    </Stack>
  );
};
